import React, { useEffect, useMemo, useState } from "react"
import toastr from "toastr"
import { Button, Input, Modal, ModalBody, ModalFooter, ModalHeader, Spinner, Table } from "reactstrap"
import { apiBuscarGruposDisponiveisMenu, apiCriarAgrupamento } from "../../helpers/backend_helper"

// Cada grupo marcado vira um agrupamento do menu, com o nome do grupo; nome e ordem só mudam depois, na edição.
export const AdicionarGruposModal = ({ menu, toggle, onSalvo, translation }) => {
  const [grupos, setGrupos] = useState([])
  const [selecionados, setSelecionados] = useState(new Set())
  const [busca, setBusca] = useState("")
  const [carregando, setCarregando] = useState(false)
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState(false)

  useEffect(() => {
    if (!menu) return undefined
    let ativo = true
    setGrupos([])
    setSelecionados(new Set())
    setBusca("")
    setErro(false)
    setCarregando(true)
    apiBuscarGruposDisponiveisMenu({ idMenu: menu.id })
      .then(lista => { if (ativo) setGrupos(lista || []) })
      .catch(() => {
        if (ativo) {
          setErro(true)
          toastr.error(translation.groupsLoadError)
        }
      })
      .finally(() => { if (ativo) setCarregando(false) })
    return () => { ativo = false }
  }, [menu])

  const visiveis = useMemo(() => {
    const termo = busca.trim().toLowerCase()
    if (!termo) return grupos
    return grupos.filter(grupo =>
      String(grupo.Codigo).includes(termo) || (grupo.Descricao || "").toLowerCase().includes(termo))
  }, [grupos, busca])

  const alternar = id => setSelecionados(anteriores => {
    const novos = new Set(anteriores)
    if (novos.has(id)) novos.delete(id)
    else novos.add(id)
    return novos
  })

  // "Selecionar todos" age só no que a busca mostra; o que ela esconde fica como estava.
  const visiveisMarcados = visiveis.filter(grupo => selecionados.has(grupo.id)).length
  const todosVisiveisMarcados = visiveis.length > 0 && visiveisMarcados === visiveis.length

  const alternarTodos = () => setSelecionados(anteriores => {
    const novos = new Set(anteriores)
    visiveis.forEach(grupo => (todosVisiveisMarcados ? novos.delete(grupo.id) : novos.add(grupo.id)))
    return novos
  })

  const fechar = () => !salvando && toggle()

  const salvar = async () => {
    setSalvando(true)
    try {
      await apiCriarAgrupamento({ idMenu: menu.id, grupos: grupos.filter(g => selecionados.has(g.id)).map(g => g.id) })
      toastr.success(translation.confirmSuccess)
      onSalvo()
    } catch (error) {
      toastr.error(error?.response?.data?.[0]?.message || translation.saveError)
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Modal isOpen={!!menu} toggle={fechar} size="md" scrollable>
      <ModalHeader toggle={fechar}>{translation.groupsAddTitle} — {menu?.nome}</ModalHeader>
      <ModalBody>
        {carregando ? (
          <div className="text-center p-3"><Spinner color="primary" /></div>
        ) : erro ? (
          <p className="text-danger mb-0">{translation.groupsLoadError}</p>
        ) : grupos.length === 0 ? (
          <p className="text-muted mb-0">{translation.groupsAllAdded}</p>
        ) : (
          <>
            <Input
              type="search"
              className="mb-3"
              placeholder={translation.groupsSearch}
              value={busca}
              onChange={e => setBusca(e.target.value)}
              disabled={salvando}
            />
            <Table size="sm" hover className="mb-0 align-middle">
              <thead>
                <tr>
                  <th style={{ width: "2.5rem" }}>
                    <Input
                      type="checkbox"
                      className="form-check-input m-0"
                      aria-label={translation.groupsSelectAll}
                      title={translation.groupsSelectAll}
                      checked={todosVisiveisMarcados}
                      innerRef={el => { if (el) el.indeterminate = visiveisMarcados > 0 && !todosVisiveisMarcados }}
                      onChange={alternarTodos}
                      disabled={salvando || visiveis.length === 0}
                    />
                  </th>
                  <th style={{ width: "5rem" }}>{translation.groupsCode}</th>
                  <th>{translation.groupsDescription}</th>
                </tr>
              </thead>
              <tbody>
                {visiveis.map(grupo => (
                  <tr key={grupo.id} style={{ cursor: salvando ? "default" : "pointer" }}
                      onClick={() => !salvando && alternar(grupo.id)}>
                    <td>
                      <Input
                        type="checkbox"
                        className="form-check-input m-0"
                        id={`grupo-${grupo.id}`}
                        aria-label={`${grupo.Codigo} - ${grupo.Descricao}`}
                        checked={selecionados.has(grupo.id)}
                        onClick={e => e.stopPropagation()}
                        onChange={() => alternar(grupo.id)}
                        disabled={salvando}
                      />
                    </td>
                    <td>{grupo.Codigo}</td>
                    <td>{grupo.Descricao}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={fechar} disabled={salvando}>{translation.modalCancelButton}</Button>
        <Button color="primary" onClick={salvar} disabled={salvando || selecionados.size === 0}>
          {translation.groupsAddButton}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
